import type {
    SwipeableRowFocusInsideConstantType,
    SwipeableRowTargetConstantType,
    SwipeableRowBoundsConstantType,
    SwipeableRowOrderConstantType,
    SwipeableRowTarget,
    ISwipeRowAction
} from "./type";

export const SWIPEABLE_ROW_MAX_TRAILING = 3;

export const SWIPEABLE_ROW_MAX_LEADING = 2;

export const SWIPEABLE_ROW_AXIS_RATIO = 1.5;

export const SWIPEABLE_ROW_RESISTANCE = 0.25;

export const SWIPEABLE_ROW_FLING_VELOCITY = 0.5;

export const SWIPEABLE_ROW_HALF = 0.5;

export const SWIPEABLE_ROW_SPRING_VELOCITY_SCALE = 1000;

export const SWIPEABLE_ROW_REST_DISPLACEMENT = 1;

export const SWIPEABLE_ROW_REST_SPEED = 20;

export const SWIPEABLE_ROW_COLLAPSE_START = 0.6;

export const getSwipeableRowOrderedActions = ({
    trailingActions,
    leadingActions
}: SwipeableRowOrderConstantType): Array<ISwipeRowAction> => {
    const allActions = [
        ...leadingActions,
        ...[
            ...trailingActions
        ].reverse()
    ];

    return [
        ...allActions.filter((action) => action.type !== "danger"),
        ...allActions.filter((action) => action.type === "danger")
    ];
};

export const getSwipeableRowBoundedOffset = ({
    isFullSwipeEnabled,
    trailingWidth,
    leadingWidth,
    rowWidth,
    value
}: SwipeableRowBoundsConstantType): number => {
    const size = value > 0 ? leadingWidth : trailingWidth;

    if(!size || !value) {
        return 0;
    }

    const absolute = Math.min(Math.abs(value), Math.max(rowWidth, size));

    if(isFullSwipeEnabled) {
        return Math.sign(value) * absolute;
    }

    const resisted = absolute > size ? size + (absolute - size) * SWIPEABLE_ROW_RESISTANCE : absolute;

    return Math.sign(value) * resisted;
};

export const getSwipeableRowTarget = ({
    isFullSwipeEnabled,
    trailingWidth,
    leadingWidth,
    rowWidth,
    velocity,
    value
}: SwipeableRowTargetConstantType): SwipeableRowTarget => {
    const side = value > 0 ? "leading" : "trailing";

    const width = side === "leading" ? leadingWidth : trailingWidth;

    const sign = side === "leading" ? 1 : -1;

    const absolute = Math.abs(value);

    if(!width || !value) {
        return {
            kind: "close",
            offset: 0
        };
    }

    if(isFullSwipeEnabled && absolute > rowWidth * SWIPEABLE_ROW_HALF) {
        return {
            offset: sign * rowWidth,
            kind: "full",
            side
        };
    }

    const towards = velocity * sign;

    if(towards < -SWIPEABLE_ROW_FLING_VELOCITY) {
        return {
            kind: "close",
            offset: 0
        };
    }

    if(absolute > width * SWIPEABLE_ROW_HALF || towards > SWIPEABLE_ROW_FLING_VELOCITY) {
        return {
            offset: sign * width,
            kind: "open",
            side
        };
    }

    return {
        kind: "close",
        offset: 0
    };
};

export const getIsSwipeableRowFocusInside = ({
    relatedTarget,
    currentTarget
}: SwipeableRowFocusInsideConstantType): boolean => {
    if(typeof Node === "undefined" || !(currentTarget instanceof Node) || !(relatedTarget instanceof Node)) {
        return false;
    }

    return currentTarget.contains(relatedTarget);
};

export const focusSwipeableRowTarget = ({
    node
}: {
    node: unknown;
}) => {
    if(typeof HTMLElement === "undefined" || !(node instanceof HTMLElement)) {
        return;
    }

    const target = node.querySelector<HTMLElement>("a[href], button, [tabindex=\"0\"]");

    target?.focus();
};
